import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Badge. Pill status token: tinted background + matching
 * foreground, NO border. Optional leading current-color dot. Variants map
 * to the ds.css .badge--* spec (primary/green/yellow/red) plus purple/gray/teal.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-so-12 font-bold leading-none',
  {
    variants: {
      variant: {
        primary: 'bg-sober-primary-bg text-sober-primary',
        green: 'bg-sober-green-bg text-sober-green-fg',
        yellow: 'bg-sober-yellow-bg text-sober-yellow-fg',
        red: 'bg-sober-red-bg text-sober-red',
        purple: 'bg-sober-purple-bg text-sober-purple',
        teal: 'bg-[#E6F7F5] text-[#0D6A63]',
        gray: 'bg-sober-muted text-sober-secondary',
      },
    },
    defaultVariants: { variant: 'primary' },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, dot = false, children, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot ? (
        <span
          aria-hidden="true"
          className="size-[7px] shrink-0 rounded-full bg-current"
        />
      ) : null}
      {children}
    </span>
  )
);
Badge.displayName = 'Badge';

export { badgeVariants };
